import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  PlusIcon,
  TrashIcon,
  LoaderIcon,
  PencilIcon,
  RestartIcon,
  AlertCircleIcon
} from '@/components/icons'
import { agentApi } from '@/api/agent.api'
import { create } from '@bufbuild/protobuf'
import { ListRequestSchema } from '@/proto/agentplatform/httpv2/common_pb'
import { toast } from 'sonner'
import { fullErrorText } from '@/api/errors'
import { Button } from '@/components/ui/button'
import { CapabilityGate } from '@/components/deployment/CapabilityGate'
import { ArrowRight } from 'lucide-react'
import { FieldSearch, buildSearchParams, type SearchFieldOption } from '@/components/FieldSearch'
import { Card } from '@/components/ui/card'
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell
} from '@/components/ui/table'
import { Skeleton } from '@/components/ui/skeleton'
import { cn } from '@/lib/utils'
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog'

function formatTimestamp(timestamp: number | bigint | undefined) {
  if (!timestamp) return '-'
  const raw = Number(timestamp)
  if (!Number.isFinite(raw)) return '-'

  // Auto-detect timestamp unit from backend: s / ms / ns.
  const millis =
    raw > 1e14
      ? Math.floor(raw / 1e6) // ns -> ms
      : raw > 1e11
        ? raw // ms
        : raw * 1000 // s -> ms

  const date = new Date(millis)
  if (Number.isNaN(date.getTime())) return '-'
  return date.toLocaleString('zh-CN', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false
  })
}

const AGENT_SEARCH_FIELDS: SearchFieldOption[] = [
  { label: '名称', key: 'name', match: 'fuzzy' },
  { label: '描述', key: 'description', match: 'fuzzy' },
  { label: 'ID', key: 'id', match: 'fuzzy' }
]

export default function AgentsPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(20)
  const [searchKeyword, setSearchKeyword] = useState('')
  const [searchField, setSearchField] = useState('name')
  const [deleteAgent, setDeleteAgent] = useState<{ id: string; name: string } | null>(null)
  const [restartAgent, setRestartAgent] = useState<{ id: string; name: string } | null>(null)

  // 关键字变化后回到首页，避免停留在超出范围的页码
  useEffect(() => {
    setPage(1)
  }, [searchKeyword])

  // 关键字变化后回到首页，避免停留在超出范围的页码
  useEffect(() => {
    setPage(1)
  }, [searchKeyword])

  const searchParams = buildSearchParams(searchField, searchKeyword, AGENT_SEARCH_FIELDS)
  const searchKey = JSON.stringify(searchParams)

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['agents', page, pageSize, searchKey],
    queryFn: () =>
      agentApi.listAgents(
        create(ListRequestSchema, {
          page,
          pageSize,
          fuzzy: searchParams.fuzzy,
          filter: searchParams.filter
        })
      )
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string) => agentApi.deleteAgent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] })
      setDeleteAgent(null)
    },
    onError: err => {
      toast.error(`删除失败：${fullErrorText(err)}`)
      setDeleteAgent(null)
    }
  })

  const handleDelete = (agent: { id: string; name: string }) => {
    setDeleteAgent(agent)
  }

  const confirmDelete = () => {
    if (deleteAgent) {
      deleteMutation.mutate(deleteAgent.id)
    }
  }

  const restartMutation = useMutation({
    mutationFn: (id: string) => agentApi.restartAgent(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agents'] })
      toast.success('已重启，若模版参数已变更请手动更新实例', {
        icon: <RestartIcon className="h-4 w-4 text-primary" />
      })
      setRestartAgent(null)
    },
    onError: err => {
      toast.error(`重启失败：${fullErrorText(err)}`, {
        icon: <AlertCircleIcon className="h-4 w-4 text-destructive" />
      })
      setRestartAgent(null)
    }
  })

  const confirmRestart = () => {
    if (restartAgent) {
      restartMutation.mutate(restartAgent.id)
    }
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold tracking-tight">智能体模版</h2>
          <p className="text-sm text-muted-foreground mt-1">
            管理和配置智能体模版，用于创建智能体实例
          </p>
        </div>
        <div className="flex items-center gap-3">
          <FieldSearch
            fields={AGENT_SEARCH_FIELDS}
            field={searchField}
            onFieldChange={setSearchField}
            keyword={searchKeyword}
            onKeywordChange={setSearchKeyword}
          />
          <CapabilityGate capability="agent.templates.create">
            <Button onClick={() => navigate('/agents/create')} className="gap-2">
              <PlusIcon className="h-4 w-4" />
              创建智能体模版
            </Button>
          </CapabilityGate>
        </div>
      </div>

      <Card className="overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead className="w-[35%] h-12 bg-muted/40">名称</TableHead>
              <TableHead className="w-[25%] bg-muted/40">描述</TableHead>
              <TableHead className="w-[15%] bg-muted/40">创建时间</TableHead>
              <TableHead className="w-[15%] bg-muted/40">更新时间</TableHead>
              <TableHead className="w-[10%] bg-muted/40 text-left">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              Array.from({ length: 5 }).map((_, i) => (
                <TableRow
                  key={i}
                  className="animate-fade-in-up"
                  style={{ animationDelay: `${i * 50}ms` }}
                >
                  <TableCell>
                    <Skeleton className="h-5 w-32 mb-2" />
                    <Skeleton className="h-3 w-20" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-full max-w-[200px]" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-24 mx-auto" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-4 w-32" />
                  </TableCell>
                  <TableCell>
                    <Skeleton className="h-8 w-20" />
                  </TableCell>
                </TableRow>
              ))
            ) : error ? (
              <TableRow>
                <TableCell colSpan={5} className="h-64">
                  <div className="flex flex-col items-center justify-center gap-4 animate-fade-in">
                    <div className="w-16 h-16 rounded-full bg-destructive/10 flex items-center justify-center">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-8 w-8 text-destructive"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                        />
                      </svg>
                    </div>
                    <div className="text-center">
                      <p className="font-medium">加载失败</p>
                      <p className="text-sm text-muted-foreground mt-1">请检查网络连接后重试</p>
                    </div>
                    <Button variant="outline" onClick={() => refetch()} className="gap-2">
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-4 w-4"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                        strokeWidth={2}
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                        />
                      </svg>
                      重试
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : !data?.data?.length ? (
              <TableRow>
                <TableCell colSpan={5} className="h-80">
                  <div className="flex flex-col items-center justify-center gap-5 animate-fade-in">
                    <div className="relative">
                      <div className="w-24 h-24 rounded-full bg-gradient-to-br from-primary/20 to-primary/5 flex items-center justify-center">
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="h-12 w-12 text-primary/60"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                          strokeWidth={1.5}
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                          />
                        </svg>
                      </div>
                      <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-background border-2 border-primary/20 flex items-center justify-center">
                        <PlusIcon className="h-4 w-4 text-primary" />
                      </div>
                    </div>
                    <div className="text-center">
                      <p className="text-lg font-medium">
                        {searchKeyword.trim() ? '没有找到匹配的模版' : '暂无 Agent'}
                      </p>
                      <p className="text-sm text-muted-foreground mt-1">
                        {searchKeyword.trim()
                          ? '尝试其他关键词搜索'
                          : '创建第一个 Agent 来开启智能体之旅'}
                      </p>
                    </div>
                  </div>
                </TableCell>
              </TableRow>
            ) : (
              data.data.map((agent, index) => (
                <TableRow
                  key={agent.id}
                  className={cn('group transition-all duration-200', 'hover:bg-primary/5')}
                  style={{ animationDelay: `${index * 30}ms` }}
                >
                  <TableCell className="font-medium py-4">
                    <div className="min-w-0">
                      <button
                        type="button"
                        className="text-primary hover:text-primary/80 font-medium text-left truncate block"
                        onClick={() => navigate(`/agents/${agent.id}`)}
                      >
                        {agent.name}
                      </button>
                      <span className="text-xs text-muted-foreground/60">ID: {agent.id}</span>
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground truncate max-w-0">
                    <span title={agent.description || ''} className="block truncate">
                      {agent.description || (
                        <span className="text-muted-foreground/40">暂无描述</span>
                      )}
                    </span>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatTimestamp(agent.createdAt)}
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatTimestamp(agent.updatedAt)}
                  </TableCell>
                  <TableCell onClick={e => e.stopPropagation()}>
                    <div className="flex items-center gap-1">
                      <CapabilityGate capability="agent.instances.create">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs gap-1"
                          onClick={() => navigate(`/agents/${agent.id}/create-instance`)}
                        >
                          <PlusIcon className="h-3 w-3" />
                          创建实例
                        </Button>
                      </CapabilityGate>
                      <CapabilityGate capability="agent.templates.restart">
                        <Button
                          variant="outline"
                          size="sm"
                          className="h-7 text-xs gap-1"
                          onClick={() => setRestartAgent({ id: agent.id, name: agent.name })}
                        >
                          <RestartIcon className="h-3 w-3" />
                          重启
                        </Button>
                      </CapabilityGate>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        onClick={() => navigate(`/agents/${agent.id}/edit`)}
                      >
                        <PencilIcon className="h-3.5 w-3.5" />
                      </Button>
                      <CapabilityGate capability="agent.templates.delete">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => handleDelete({ id: agent.id, name: agent.name })}
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </Button>
                      </CapabilityGate>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-7 w-7 text-muted-foreground hover:text-foreground"
                        aria-label="查看智能体模板详情"
                        onClick={() => navigate(`/agents/${agent.id}`)}
                      >
                        <ArrowRight className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {data?.data?.length ? (
          <div className="flex items-center justify-between px-6 py-4 border-t bg-muted/20">
            <span className="text-sm text-muted-foreground">共 {data.total} 条</span>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5">
                <span className="text-sm text-muted-foreground">每页</span>
                <select
                  value={pageSize}
                  onChange={e => {
                    setPageSize(Number(e.target.value))
                    setPage(1)
                  }}
                  className="h-7 rounded-md border border-input bg-background px-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring cursor-pointer"
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
                <span className="text-sm text-muted-foreground">条</span>
              </div>
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={page <= 1}
                  onClick={() => setPage(p => p - 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                  </svg>
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 w-7 p-0"
                  disabled={page >= Math.ceil(data.total / pageSize)}
                  onClick={() => setPage(p => p + 1)}
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="h-4 w-4"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                    strokeWidth={2}
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                  </svg>
                </Button>
              </div>
            </div>
          </div>
        ) : null}
      </Card>

      <AlertDialog open={!!deleteAgent} onOpenChange={() => setDeleteAgent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>确认删除</AlertDialogTitle>
            <AlertDialogDescription>
              确定要删除 Agent "{deleteAgent?.name}" 吗？此操作不可撤销。
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setDeleteAgent(null)}>取消</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={confirmDelete}
              disabled={deleteMutation.isPending}
            >
              {deleteMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}
              删除
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={!!restartAgent} onOpenChange={() => setRestartAgent(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>重启智能体模版</AlertDialogTitle>
            <AlertDialogDescription>
              重启将同步最新配置到运行实例，若模版参数已变更，需手动更新实例以使配置生效。确认重启「
              {restartAgent?.name}」？
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={() => setRestartAgent(null)}>取消</AlertDialogCancel>
            <Button onClick={confirmRestart} disabled={restartMutation.isPending}>
              {restartMutation.isPending && <LoaderIcon className="mr-2 h-4 w-4" />}
              确认重启
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
